'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useState } from 'react';

/** The tab the address names, or `initial` when it names none of them. */
const tabInAddress = <T extends string,>(tabs: readonly T[], initial: T): T => {
  const named = globalThis.location.hash.slice(1);
  return tabs.find(candidate => candidate === named) ?? initial;
};

/**
 * The open tab, kept in the address as `#material` and the like, so a reload opens it again and
 * a link can point at it. Leaving the overview is a history entry of its own, so the back button
 * returns to it after following a to-do into a form; moving on between the other tabs replaces
 * that entry, so the next step back still leaves the page.
 */
export const useTabInAddress = (tabs, initial) => {
  const $ = _c(12);
  let t0;
  if ($[0] !== initial || $[1] !== tabs) {
    t0 = () => {
      if ((globalThis as {
        window?: unknown;
      }).window === undefined) {
        return initial;
      }
      return tabInAddress(tabs, initial);
    };
    $[0] = initial;
    $[1] = tabs;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const [tab, setTab] = useState(t0);
  let t1;
  let t2;
  if ($[3] !== initial || $[4] !== tabs) {
    t1 = () => {
      const follow = () => setTab(tabInAddress(tabs, initial));
      globalThis.addEventListener("popstate", follow);
      return () => globalThis.removeEventListener("popstate", follow);
    };
    t2 = [tabs, initial];
    $[3] = initial;
    $[4] = tabs;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
  let t3;
  if ($[7] !== initial) {
    t3 = next => {
      setTab(next);
      const {
        pathname,
        search,
        hash
      } = globalThis.location;
      const target = next === initial ? "" : `#${next}`;
      if (hash === target) {
        return;
      }
      const url = `${pathname}${search}${target}`;
      if (hash === "") {
        globalThis.history.pushState(undefined, "", url);
      } else {
        globalThis.history.replaceState(undefined, "", url);
      }
    };
    $[7] = initial;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const select = t3;
  let t4;
  if ($[9] !== select || $[10] !== tab) {
    t4 = [tab, select];
    $[9] = select;
    $[10] = tab;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
};